import { z } from 'zod';
import { BOARD_VIEW_KINDS, boardViewKindSchema } from './view-state';

/**
 * Per-member pinned board views — `FR-VIEW-004`.
 *
 * One member's ordered set of views pinned to the board header, and the
 * contract for `GET`/`PUT /boards/:id/pinned-views`. The state is private to
 * the caller: `board_pinned_views`'s primary key is
 * `(user_id, board_id, view_kind)` and the migration that created it
 * (`0005_views_and_search.sql`) states that pinning "never alters the board
 * header for anyone else". So this contract emits no event, no activity entry,
 * no notification and no socket broadcast — a broadcast here would leak one
 * member's private chrome to every socket on the board.
 *
 * ⚠ The vocabulary is imported, never restated. `boardViewKindSchema` is
 * `./view-state`'s, and `BOARD_VIEW_KINDS` is character-for-character
 * `ck_board_pinned_views_view_kind`. Nothing in `./view-state` is widened:
 * `boardViewPreferenceSchema` is a `z.strictObject` serialising both the body
 * and the response of `PUT /boards/:id/view`, so a `pinnedViews` key added
 * there would break that route in both directions. This is a separate module
 * for that reason.
 *
 * ⚠ `views` is an ORDER, not a set. It is returned in pinned order and
 * accepted in pinned order; do not add a `.transform()` that sorts it. The
 * `position` lexorank that backs the order is assigned server-side and never
 * appears on the wire, which keeps the `COLLATE "C"` trap inside the server.
 *
 * ⚠ This one schema serialises three things: the `PUT` body, the `PUT`
 * response and the `GET` response. A constraint that rejects a bad body with a
 * 422 fails RESPONSE serialisation with a 500 if stored rows can ever violate
 * it. Two of the three constraints here are already enforced by the database
 * (`ck_board_pinned_views_view_kind`, and the primary key for uniqueness); the
 * length cap is not, so the `PUT` handler must fully REPLACE the member's
 * pinned set — deleting every row whose kind is absent from the body, in the
 * same transaction as the upserts — rather than upserting the complement. A
 * full replace makes the cap unreachable by construction rather than by luck,
 * and it is what makes `{ views: [] }` a legal body that clears every pin.
 */

function hasUniqueValues(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

export const boardPinnedViewsSchema = z.strictObject({
  views: z
    .array(boardViewKindSchema)
    .max(BOARD_VIEW_KINDS.length)
    .refine(hasUniqueValues, {
      message: 'Pinned views must not contain duplicates.',
    }),
});

export type BoardPinnedViews = z.infer<typeof boardPinnedViewsSchema>;
